import { useEffect, useMemo } from 'react';
import { Colors, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { Pagination } from 'components/layout/List/Paging';
import PastProjects from 'components/widgets/ProjectDetails/PastProjects';
import { usePaginate } from 'hooks/usePaginate';
import { useAddressQuery } from 'state/queryParams';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import type { PaginatedProject } from 'types';

const paginationContainer = {
  mt: 'padding_16',
};

const containerStyle = {
  width: '100%',
};

const labelStyle = {
  color: Colors.TextQuiet,
};

type Props = {
  projectSlug?: string;
  projectAddress: string;
  isLoading?: boolean;
  projectsList: PaginatedProject[];
  setPage: (page: number) => void;
  count: number;
};

const PastProjectsSection = ({
  projectSlug,
  projectAddress,
  isLoading = false,
  projectsList = [],
  setPage,
  count = 0,
}: Props) => {
  const [address, setAddress] = useAddressQuery();
  const { page, nextPage, previousPage, setPageNumber } = usePaginate();
  const PAGE_SIZE = 5;
  const totalPages = (): number => {
    return count ? Math.ceil(count / PAGE_SIZE) : 1;
  };

  const isSomethingLoading = useMemo(() => {
    return isLoading;
  }, [isLoading]);

  const filterProjectList = useMemo(() => {
    if (projectsList && projectsList.length > 0) {
      return projectsList.filter((item) => item.slug !== projectSlug);
    }

    return [];
  }, [projectSlug, projectsList]);

  useEffect(() => {
    if (!address) {
      setAddress(projectAddress || '');
    }
  }, [setAddress, projectAddress]);

  useEffect(() => {
    if (page) {
      setPage(page);
    }
  }, [page, setPage]);

  if (filterProjectList && filterProjectList.length === 0 && !isSomethingLoading) {
    return (
      <FlexColumn sx={containerStyle}>
        <PastProjects
          projectsList={filterProjectList}
          loading={isSomethingLoading}
          mapLoading={isSomethingLoading}
          leadContext={{
            groupId: projectSlug,
            groupType: ProjectGroupTypes.OTHER_PROJECTS,
          }}
        >
          Other Projects at This Address
        </PastProjects>
        <Text.Body sx={labelStyle}>No other projects found at this location</Text.Body>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn sx={containerStyle}>
      <PastProjects
        projectsList={filterProjectList}
        loading={isSomethingLoading}
        mapLoading={isSomethingLoading}
        leadContext={{
          groupId: projectSlug,
          groupType: ProjectGroupTypes.OTHER_PROJECTS,
        }}
      >
        Other Projects at This Address
      </PastProjects>
      {totalPages && totalPages() > 1 && (
        <Pagination
          currentPage={page}
          isLoading={isSomethingLoading}
          setPage={(pageNumber) => setPageNumber(pageNumber)}
          totalPages={totalPages()}
          onNext={nextPage}
          onPrev={previousPage}
          sx={paginationContainer}
        />
      )}
    </FlexColumn>
  );
};

export default PastProjectsSection;
